/**
 * Inline Editor Module
 * Text editing, color picking, and HTML utilities extracted from main.js.
 */

// ============================================
// Color palette (from original main.js)
// ============================================

const COLORS = [
  "#3b82f6",
  "#ef4444",
  "#f59e0b",
  "#10b981",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#6366f1",
  "#84cc16",
  "#f97316",
  "#14b8a6",
  "#a855f7",
  "#e11d48",
  "#0ea5e9",
  "#eab308",
  "#22c55e",
];

// ============================================
// Callbacks (injected via setupInlineEditor)
// ============================================

let cb = null;

/**
 * Initialize the inline editor module with Store/CRDT callbacks.
 * @param {Object} callbacks
 * @param {Function} callbacks.findNode - Store.find(id)
 * @param {Function} callbacks.updateNode - CRDTBridge.updateNode(id, updates)
 */
export function setupInlineEditor(callbacks) {
  cb = callbacks;
}

// ============================================
// HTML Utilities
// ============================================

// Canonical implementation lives in engine/core/validation.js; re-exported here
// because external callers import escapeHtml from this module.
export { escapeHtml } from "../../../engine/core/validation.js";

// ============================================
// Theme / Color Utilities
// ============================================

export function getThemeApi() {
  if (typeof window === "undefined") return null;
  return window.OrbitalTheme || null;
}

export function getCurrentPalette() {
  const themeApi = getThemeApi();
  if (themeApi && typeof themeApi.getCurrentColors === "function") {
    return themeApi.getCurrentColors();
  }
  return COLORS;
}

export function getNewColor(existingColors, excludeColor) {
  // Find a color not used by siblings
  const palette = getCurrentPalette();
  const available = palette.filter(
    (c) => !existingColors.includes(c) && c !== excludeColor,
  );
  if (available.length > 0) {
    return available[0]; // Pick first available for consistency
  }
  // Fallback: pick any color different from exclude
  const fallback = palette.filter((c) => c !== excludeColor);
  return fallback[Math.floor(Math.random() * fallback.length)] || palette[0];
}

// ============================================
// Inline Editing
// ============================================

/**
 * Make a label element editable inline with a textarea overlay.
 * Requires setupInlineEditor() to have been called first.
 */
export function makeEditable(labelEl, nodeId, viewType = "mindmap", mouseX, mouseY) {
  console.log("[InlineEditor] makeEditable called:", nodeId, viewType, labelEl);
  const node = cb.findNode(nodeId);
  if (!node) {
    console.log("[InlineEditor] makeEditable: node not found");
    return;
  }

  const originalText = labelEl.textContent;
  const input = document.createElement("textarea");
  input.value = originalText;

  // Calculate line height and max height for 10 lines
  const lineHeight = 20; // Approximate line height in px
  const maxLines = 10;
  const maxHeight = lineHeight * maxLines + 12; // +12 for padding

  // Auto-resize function
  const autoResize = () => {
    // Reset height to auto to get the scroll height
    input.style.height = 'auto';
    const scrollHeight = input.scrollHeight;
    if (scrollHeight <= maxHeight) {
      input.style.height = scrollHeight + 'px';
      input.style.overflowY = 'hidden';
    } else {
      input.style.height = maxHeight + 'px';
      input.style.overflowY = 'auto';
    }
  };

  // Base styles for textarea
  const baseStyles = `
    font-size: 14px;
    font-family: inherit;
    color: #1a1d24;
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid #3b82f6;
    border-radius: 4px;
    padding: 6px 10px;
    outline: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    resize: none;
    line-height: ${lineHeight}px;
    overflow-y: hidden;
  `;

  // For bubble view, always use overlay input (immune to re-renders)
  if (viewType === "bubble" && mouseX !== undefined && mouseY !== undefined) {
    // Position input at mouse pointer
    input.style.cssText = `
      ${baseStyles}
      position: fixed;
      left: ${mouseX - 150}px;
      top: ${mouseY - 15}px;
      width: 300px;
      min-height: ${lineHeight + 12}px;
      max-height: ${maxHeight}px;
      z-index: 10000;
      text-align: center;
    `;
    document.body.appendChild(input);
  } else {
    // Original inline replacement for leaf nodes and mindmap.
    // position:relative + z-index:2 keeps the textarea above the
    // .mm-node::before dim overlay (z-index 1) used by tag-spotlight,
    // so what the user is typing isn't obscured by the highlight fade.
    input.style.cssText = `
      ${baseStyles}
      width: 300px;
      min-width: 200px;
      min-height: ${lineHeight + 12}px;
      max-height: ${maxHeight}px;
      position: relative;
      z-index: 2;
    `;
    labelEl.textContent = "";
    labelEl.appendChild(input);
  }

  input.focus({ preventScroll: true });
  input.select();

  // Initial resize
  setTimeout(autoResize, 0);

  // Resize on input
  input.addEventListener("input", autoResize);

  const save = () => {
    // Prevent double-execution (Enter triggers save, then blur fires)
    if (input._saved) return;
    input._saved = true;

    const newValue = input.value.trim();
    if (newValue && newValue !== originalText) {
      // Codex-2: Check if update succeeds (may fail for read-only shares)
      const success = cb.updateNode(nodeId, { name: newValue });
      if (!success) {
        // Revert to original on failure
        if (input.parentNode === document.body) {
          input.remove();
        } else {
          labelEl.textContent = originalText;
        }
        return;
      }
    }

    // Clean up overlay input if used
    if (input.parentNode === document.body) {
      input.remove();
    } else {
      labelEl.textContent = newValue || originalText;
    }

    // Phase 3.2 Fix: Manual view.update() removed - redundant with CRDT-first architecture
    // The update flow is: CRDTBridge.updateNode() → CRDT → Observer → Store._setDataFromCRDT()
    //                     → Store subscriber → renderTree() + view.update()
  };

  const cancel = () => {
    // Prevent double-execution (Escape triggers cancel, then blur fires)
    if (input._saved) return;
    input._saved = true;

    // Clean up overlay input if used
    if (input.parentNode === document.body) {
      input.remove();
    } else {
      labelEl.textContent = originalText;
    }
  };

  input.addEventListener("blur", save);
  input.addEventListener("keydown", (e) => {
    // Enter without modifier saves, Shift+Enter adds newline
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  });
}
